import { UPLOADS_DIRECTORY } from "../constants/config";
import {cms} from "../services/api";

let cached = null;

/**
 * Returns all authors.
 *
 * @example <caption>Response</caption>
 * [
 *   {
 *     id: 1,
 *     attributes: {
 *       name: "dr. XXX XXX",
 *       description: "",
 *       image: {...}
 *     }
 *   },
 *   {
 *     id: 2,
 *     [...]
 *   }
 * ]
 *
 * @return {Promise<Array<{id: number, attributes: {name: string, description: string, image: Object}}>|null>} Authors
 * data
 */
export async function getAuthors() {
  try {
    if(!cached) {
      const response = await cms.get("/authors?populate=*&pagination[limit]=500");
      if(response.status !== 200) {
        return null;
      }

      cached = response.data.data;
    }

    return cached;
  } catch(error) {
    console.error(error);
  }

  return null;
}

/**
 * Returns a single author data.
 *
 * @example <caption>Response</caption>
 * {
 *   name: "dr. XXX XXX",
 *   description: "",
 *   image: {...}
 * }
 *
 * @param {number} id ID of the author to fetch.
 * @return {Promise<{name: string, description: string, image: Object}|null>} Author data.
 */
export async function getAuthor(id) {
  const fetchAuthor = async() => {
    try {
      const response = await cms.get(`/authors/${id}?populate=*`);
      if(response.status !== 200) {
        return null;
      }

      if(!cached) {
        cached = [];
      }

      cached.push(response.data.data);
      return response.data.data.attributes;
    } catch(error) {
      console.error(error);
      return null;
    }
  };

  if(cached) {
    let author = cached.find(el => el.id === id);

    if(!author) {
      return await fetchAuthor();
    }

    return author.attributes;
  }

  return await fetchAuthor();
}

/**
 * Returns all authors, but in changed structure (array of authors containing just name, description and image (url))
 *
 * @example <caption>Response</caption>
 * [
 *  {
 *    name: "dr. XXX XXX",
 *    description: "",
 *    image: "https://example.com/person.png"
 *  }
 * ]
 *
 * @return {Promise<Array<{name: string, description: string, image: string}>|null>} Authors array.
 */
export async function fetchAuthors() {
  const authors = [];

  try {
    const data = await getAuthors();

    for(const author of data) {
      const description = author.attributes.description
                                 .replaceAll('\n', '<br>');

      authors.push({
                      name:    author.attributes.name,
                      description: description,
                      image:   `${UPLOADS_DIRECTORY}${author.attributes.image.data.attributes.url}`
                    });
    }

    return authors;
  } catch(error) {
    console.error(error);

    return null;
  }
};
